// Phase 4.3 — Online Billing list table.
//
// Replaces the MUI <Table> + antd Dropdown/Menu action in onlineBilling.tsx with
// the shared DataTable, StatusBadge and a DropdownMenu. Presentation only: search
// + server pagination reuse the screen's handlers; the row Edit action opens the
// status Sheet via the screen's `onEdit(item)`. Order Status and Payment Status
// render as StatusBadges; ₹ amount formatting is preserved.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";
import moment from "moment";
import { DataTable } from "../../shared/data-table";
import { StatusBadge } from "../../shared/status-badge";
import { Button } from "ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";
import AppConstant from "../../Globals/AppConstants";

export interface OnlineBillingTableProps {
  data: any[];
  loading: boolean;
  onEdit: (item: any) => void;
  searchValue?: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  toolbar?: React.ReactNode;
  title?: React.ReactNode;
  headerActions?: React.ReactNode;
  pagination: { page: number; total: number; onChange: (page: number) => void };
}

export function OnlineBillingTable({
  data,
  loading,
  onEdit,
  searchValue,
  onSearch,
  searchPlaceholder,
  toolbar,
  title,
  headerActions,
  pagination,
}: OnlineBillingTableProps) {
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "orderNo", header: "Order Id", cell: ({ row }) => row.original?.orderNo },
      {
        id: "orderDate",
        header: "Purchased Date",
        cell: ({ row }) => (row.original?.orderDate ? moment(row.original?.orderDate).format("DD-MMM-YYYY") : "-"),
      },
      { id: "customerName", header: "Customer Name", cell: ({ row }) => row.original?.customerName },
      { id: "mobileNumber", header: "Mobile Number", cell: ({ row }) => row.original?.mobileNumber },
      { id: "quantity", header: "Items", cell: ({ row }) => <span className="tabular-nums">{row.original?.quantity}</span> },
      {
        id: "totalamount",
        header: "Total Amount",
        cell: ({ row }) => <span className="tabular-nums">₹ {row.original?.totalamount}</span>,
      },
      { id: "statusName", header: "Order Status", cell: ({ row }) => <StatusBadge status={row.original?.statusName} /> },
      {
        id: "paymentStatus",
        header: "Payment Status",
        cell: ({ row }) => <StatusBadge status={row.original?.paymentStatus == 1 ? "Success" : "Failed"} />,
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Row actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(item)}>{AppConstant?.edit}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <DataTable
      columns={columns}
      data={data || []}
      loading={loading}
      searchable
      searchValue={searchValue}
      onSearch={onSearch}
      searchPlaceholder={searchPlaceholder}
      toolbar={toolbar}
      title={title}
      headerActions={headerActions}
      pagination={pagination}
    />
  );
}

export default OnlineBillingTable;
